/**
 * Canonical work queue client for AgenticRCM and AgenticRcmAgency shells.
 * Task Manager (8051) is the list SSOT. Combined / RCM aggregation helpers remain for
 * ops/backfill only — the Work Queue shell loads Task Manager items exclusively.
 */

import axios from 'axios';
import {
  RCM_QUEUE_SERVICES,
  getQueueItemsFromRcmService,
  buildDisplayQueueId,
  getServiceBaseUrl,
} from './rcmQueueAggregation.js';
import {
  broadcastWorkQueueChanged,
  subscribeWorkQueueSync,
  RCM_WORKQUEUE_SYNC_CHANNEL,
} from './workQueueSync.js';
import {
  ComplianceGuardrailError,
  actionRequiresComplianceGuardrail,
  buildGuardrailPayloadFromQueueItem,
  enforceComplianceGuardrail,
  fetchComplianceGuardrailCheck,
  getComplianceApiBaseUrl,
  getShellActor,
} from './complianceGuardrail.js';
import { applyActiveWorkQueueStatusFilter } from './workQueueStatus.js';
import { applyAssignmentTypeFilter } from './workQueueAnalytics.js';
import { isSyntheticWorkQueueId } from './workQueueLink.js';

export { broadcastWorkQueueChanged, subscribeWorkQueueSync, RCM_WORKQUEUE_SYNC_CHANNEL };
export {
  ComplianceGuardrailError,
  actionRequiresComplianceGuardrail,
  buildGuardrailPayloadFromQueueItem,
  enforceComplianceGuardrail,
  fetchComplianceGuardrailCheck,
  getComplianceApiBaseUrl,
  getShellActor,
} from './complianceGuardrail.js';

function useWorkQueueMock() {
  return process.env.REACT_APP_WORKQUEUE_USE_MOCK === 'true';
}

function extractDomainIdFromQueueItem(item) {
  if (!item || typeof item !== 'object') return null;
  const m = item.metadata && typeof item.metadata === 'object' ? item.metadata : {};
  if (m.domain_id != null && String(m.domain_id).length) return String(m.domain_id);
  for (const key of ['claim_id', 'charge_id', 'eligibility_request_id', 'request_id', 'eob_id', 'posting_id']) {
    if (m[key] != null && String(m[key]).length) return String(m[key]);
    if (item[key] != null && String(item[key]).length) return String(item[key]);
  }
  const id = String(item.id || '');
  let match = id.match(/^denial_mgmt-(.+)$/);
  if (match) return match[1];
  match = id.match(/^remittance_exc-(.+)$/);
  if (match) return match[1];
  return null;
}

/** Normalize service aliases so Task Manager + domain rows dedupe correctly. */
function normalizeQueueSourceService(src) {
  const s = String(src || 'unknown').toLowerCase().trim().replace(/-/g, '_');
  const aliases = {
    denials: 'denial_management',
    denial: 'denial_management',
    denialmanagement: 'denial_management',
    claims: 'claims_processing',
    claimsprocessing: 'claims_processing',
    claim_processing: 'claims_processing',
    ar: 'ar_posting',
    arposting: 'ar_posting',
    remittance: 'remittance_advice',
    remittanceadvice: 'remittance_advice',
    eob: 'remittance_advice',
    patient_billing: 'patient_billing',
    patientbilling: 'patient_billing',
    patient_account: 'patient_billing',
    patientaccount: 'patient_billing',
    contract_management: 'contract_management',
    contract: 'contract_management',
    contracts: 'contract_management',
    rcm_compliance: 'rcm_compliance',
    compliance: 'rcm_compliance',
    insurance: 'insurance_verification',
    insuranceverification: 'insurance_verification',
    priorauth: 'prior_authorization',
    prior_auth: 'prior_authorization',
    chargecapture: 'charge_capture',
    charge: 'charge_capture',
  };
  return aliases[s] || s;
}

/** Stable dedupe across Task Manager + RCM pulls for the same underlying record */
export function canonicalQueueDedupKey(item) {
  const src = normalizeQueueSourceService(item.source_service || item.external_service || 'unknown');
  const domain = extractDomainIdFromQueueItem(item);
  if (domain) return `${src}|domain:${domain}`;
  if (item.display_queue_id) return `${src}|disp:${item.display_queue_id}`;
  const ext = item.external_id;
  if (ext != null && String(ext).length && !isUuidString(String(ext))) {
    return `${src}|ext:${String(ext)}`;
  }
  return `${src}|id:${item.id}`;
}

// Task Manager API (default port 8051). Prefer shell nginx proxy on remote/IP hosts.
// Build-time: REACT_APP_TASK_MANAGER_API_URL=https://api.example.com/taskmanager  (no trailing slash)
function shouldUseShellApiProxy() {
  if (process.env.REACT_APP_USE_DEFAULT_RCM_PORTS === 'true') return false;
  if (typeof window === 'undefined' || !window.location) return false;
  const { hostname, port } = window.location;
  const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
  if (isLocal && (port === '3000' || port === '3001')) return false;
  if (isLocal && port !== '3160' && port !== '3161' && port !== '80' && port !== '443' && port !== '') return false;
  return true;
}

const getWorkQueueApiUrl = () => {
  const explicit = process.env.REACT_APP_TASK_MANAGER_API_URL;
  if (explicit && /^https?:\/\//i.test(String(explicit).trim())) {
    return String(explicit).trim().replace(/\/$/, '');
  }
  if (shouldUseShellApiProxy()) {
    // AgenticRCM nginx: /rcm-api/task-manager/* → taskmanager_backend:8051
    return '/rcm-api/task-manager';
  }
  if (typeof window !== 'undefined' && window.location) {
    const host = window.location.hostname;
    const protocol = window.location.protocol;
    return `${protocol}//${host}:8051`;
  }
  return 'http://localhost:8051';
};

// Get API base URL dynamically (called on each request to handle different environments)
const getApiBaseUrl = () => {
  return getWorkQueueApiUrl();
};

// Create axios instance with dynamic baseURL
const workqueueClient = axios.create({
  baseURL: 'http://localhost:8051', // Default, will be overridden in interceptor
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
workqueueClient.interceptors.request.use(
  (config) => {
    // Update baseURL dynamically based on current hostname
    const baseURL = getApiBaseUrl();
    config.baseURL = baseURL;
    
    // Try both 'auth_token' (SaaSFoundation) and 'token' (fallback)
    const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add organization_id to all requests if available (do not duplicate if caller set it)
    const organizationId =
      localStorage.getItem('selectedOrganizationId') ||
      localStorage.getItem('organizationId') ||
      localStorage.getItem('organization_id');
    if (organizationId) {
      config.params = {
        ...config.params,
      };
      if (!config.params.organization_id) {
        config.params.organization_id = organizationId;
      }
    } else {
      console.warn('[WorkQueue] No organization_id found in localStorage');
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

const isTaskManagerUnreachableError = (error) => {
  const code = error?.code;
  const msg = error?.message || '';
  return (
    code === 'ERR_NETWORK' ||
    code === 'ERR_CONNECTION_REFUSED' ||
    code === 'ECONNREFUSED' ||
    msg.includes('Network Error') ||
    msg.includes('Connection refused') ||
    msg.includes('Failed to fetch')
  );
};

/** Wrong base URL (e.g. shell :3160) often returns 404 from nginx/SaaS; avoid noisy error logs. */
const isSoftWorkQueueHttpError = (error) => {
  const s = error?.response?.status;
  return s === 404 || s === 502 || s === 503;
};

// Response interceptor — avoid console.error when Task Manager is simply not deployed / port closed
workqueueClient.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (isTaskManagerUnreachableError(error) || isSoftWorkQueueHttpError(error)) {
      if (process.env.NODE_ENV === 'development') {
        console.debug(
          '[WorkQueue] Task Manager unreachable or HTTP error (start Task Manager on :8051 or set REACT_APP_TASK_MANAGER_API_URL):',
          error?.code || error?.response?.status || error?.message
        );
      }
    } else {
      console.error('WorkQueue API Error:', error);
    }
    return Promise.reject(error);
  }
);

/** RCM backends that expose a queue list (shared with AgenticRcmAgency). */
const RCM_SERVICES = RCM_QUEUE_SERVICES;

/** Human labels for source badges */
const SOURCE_SERVICE_LABELS = {
  insurance_verification: 'Insurance Verification',
  prior_authorization: 'Prior Authorization',
  claims_processing: 'Claims Processing',
  denial_management: 'Denial Management',
  charge_capture: 'Charge Capture',
  ar_posting: 'AR Posting',
  remittance_advice: 'Remittance Advice',
  patient_billing: 'Patient Billing',
  contract_management: 'Contract Management',
  rcm_compliance: 'RCM Compliance',
  taskmanager: 'Task Manager',
};

/**
 * Normalize a Task Manager queue row for the Work Queue shell table + actions.
 * Ensures source_service, display ids, and metadata are present for lifecycle/assign/notes.
 */
export function normalizeTmQueueItemForShell(item) {
  if (!item || typeof item !== 'object') return item;
  const meta =
    (item.item_metadata && typeof item.item_metadata === 'object' ? item.item_metadata : null) ||
    (item.metadata && typeof item.metadata === 'object' ? item.metadata : {}) ||
    {};
  const data = item.item_data && typeof item.item_data === 'object' ? item.item_data : {};
  const rawSource =
    item.source_service ||
    item.external_service ||
    meta.source_service ||
    item.target_service ||
    item.type ||
    'unknown';
  const source_service = normalizeQueueSourceService(rawSource);
  const domainId =
    meta.domain_id ||
    item.external_id ||
    null;
  const title =
    item.title ||
    data.title ||
    meta.title ||
    null;
  const display_queue_id =
    item.display_queue_id ||
    meta.display_queue_id ||
    (domainId != null && String(domainId).length && !isSyntheticWorkQueueId(domainId)
      ? buildDisplayQueueId(source_service, domainId)
      : null);
  const priorityRaw = String(item.priority || data.priority || 'normal').toLowerCase();
  const priority = priorityRaw === 'medium' ? 'normal' : priorityRaw;

  return {
    ...item,
    type: item.type || meta.item_type || data.item_type,
    item_type: item.item_type || item.type || meta.item_type,
    title,
    description: item.description || data.description || '',
    priority,
    status: item.status || 'pending',
    source_service,
    source_service_name:
      item.source_service_name ||
      SOURCE_SERVICE_LABELS[source_service] ||
      titleizeServiceKey(source_service),
    external_service: item.external_service || source_service,
    external_id: item.external_id || (domainId != null ? String(domainId) : item.external_id),
    display_queue_id,
    metadata: {
      ...meta,
      domain_id: domainId != null ? String(domainId) : meta.domain_id,
      display_queue_id,
      source_service,
    },
    item_metadata: {
      ...meta,
      domain_id: domainId != null ? String(domainId) : meta.domain_id,
      display_queue_id,
      source_service,
    },
    notes: item.notes ?? data.notes ?? null,
    action_taken: item.action_taken ?? data.action_taken ?? null,
  };
}

function titleizeServiceKey(key) {
  return String(key || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function mapItemsForShell(items) {
  if (!Array.isArray(items)) return [];
  return items.map(normalizeTmQueueItemForShell);
}


/** Task Manager uses UUID keys; aggregated RCM rows use stable string ids (e.g. remittance_exc-…). */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function isUuidString(value) {
  return typeof value === 'string' && UUID_RE.test(value.trim());
}

// Get service API URL (shell nginx proxy when on :3160 / :3161 / remote)
const getServiceApiUrl = (serviceKey) => {
  const service = RCM_SERVICES[serviceKey];
  if (!service) return null;
  return getServiceBaseUrl(service.port, serviceKey);
};

/** Services that persist assignment/status via /api/v1/workqueue/{id}/assign|PATCH */
const DOMAIN_WRITEBACK_SERVICES = new Set([
  'denial_management',
  'ar_posting',
  'claims_processing',
  'charge_capture',
  'insurance_verification',
  'prior_authorization',
  'remittance_advice',
  'patient_billing',
  'contract_management',
  'rcm_compliance',
]);

async function writebackToOwningService(sourceService, domainId, { assignBody = null, patchBody = null }) {
  if (!sourceService || !domainId || !DOMAIN_WRITEBACK_SERVICES.has(sourceService)) return null;
  const client = createServiceClient(sourceService);
  if (!client) return null;
  try {
    if (assignBody) {
      return await client.post(`/api/v1/workqueue/${encodeURIComponent(domainId)}/assign`, assignBody, {
        params: { push_to_tm: false },
      });
    }
    if (patchBody) {
      return await client.patch(`/api/v1/workqueue/${encodeURIComponent(domainId)}`, patchBody, {
        params: { push_to_tm: false },
      });
    }
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.debug(`[WorkQueue] domain writeback to ${sourceService} failed:`, err?.message || err);
    }
  }
  return null;
}

// Create axios instance for service calls
const createServiceClient = (serviceKey) => {
  const baseURL = getServiceApiUrl(serviceKey);
  if (!baseURL) return null;
  
  const client = axios.create({
    baseURL: baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  // Add interceptors
  client.interceptors.request.use((config) => {
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const organizationId =
      localStorage.getItem('selectedOrganizationId') ||
      localStorage.getItem('organizationId') ||
      localStorage.getItem('organization_id');
    config.params = config.params || {};
    if (organizationId && !config.params.organization_id) {
      config.params.organization_id = organizationId;
    }

    return config;
  });
  
  client.interceptors.response.use(
    (response) => response.data,
    (error) => {
      // Silently handle network errors (connection reset, refused, etc.) - these are expected when services aren't running
      const isNetworkError = error.code === 'ERR_NETWORK' || 
                            error.code === 'ERR_CONNECTION_REFUSED' ||
                            error.code === 'ERR_CONNECTION_RESET' ||
                            error.message?.includes('Network Error') ||
                            error.message?.includes('Connection refused') ||
                            error.message?.includes('Connection reset') ||
                            error.message?.includes('ECONNRESET');
      
      // Only log unexpected errors (not network errors)
      if (!isNetworkError) {
        console.error(`[${serviceKey}] API Error:`, error);
      }
      return Promise.reject(error);
    }
  );
  
  return client;
};

export const workqueueService = {
  /**
   * Compliance guardrail pre-check (RcmCompliance) — delegates to shared complianceGuardrail module.
   */
  async complianceGuardrailCheck(payload) {
    return fetchComplianceGuardrailCheck(payload);
  },

  async _maybeEnforceComplianceGuardrail(action, { assignment, updates, itemSnapshot, extra } = {}) {
    if (!actionRequiresComplianceGuardrail({ action, assignment, updates, itemSnapshot })) {
      return null;
    }
    const payload = buildGuardrailPayloadFromQueueItem(itemSnapshot || {}, action, extra);
    return enforceComplianceGuardrail(payload);
  },

  // Helper function to add timeout to promises
  async withTimeout(promise, timeoutMs = 8000, fallback) {
    return Promise.race([
      promise,
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), timeoutMs)
      )
    ]).catch(error => {
      if (error.message === 'Request timeout') {
        if (typeof fallback === 'function') {
          return fallback();
        }
        return fallback;
      }
      throw error;
    });
  },

  /** TaskManager list only; no mock fallback (used to merge with RCM queues). */
  async _getTaskManagerQueueItemsRaw(filters = {}, orgIdOverride = null) {
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach((key) => {
        const val = filters[key];
        if (val === null || val === undefined || val === '' || key === 'organization_id') {
          return;
        }
        params.append(key, val);
      });
      const oid =
        orgIdOverride ||
        localStorage.getItem('selectedOrganizationId') ||
        localStorage.getItem('organizationId') ||
        localStorage.getItem('organization_id');
      if (oid) params.append('organization_id', oid);
      const response = await this.withTimeout(
        workqueueClient.get(`/api/v1/workqueue/items?${params.toString()}`, {
          timeout: 10000,
        }),
        12000,
        () => null
      );
      if (!response) return [];
      if (response.items && Array.isArray(response.items)) return mapItemsForShell(response.items);
      if (Array.isArray(response)) return mapItemsForShell(response);
      return [];
    } catch {
      return [];
    }
  },

  // Get queue items with filters (from TaskManager - aggregated view)
  async getQueueItems(filters = {}) {
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach((key) => {
        const val = filters[key];
        if (val === null || val === undefined || val === '') return;
        if (key === 'organization_id') return; // handled below
        params.append(key, val);
      });

      const oid =
        filters.organization_id ||
        localStorage.getItem('selectedOrganizationId') ||
        localStorage.getItem('organizationId') ||
        localStorage.getItem('organization_id');
      if (oid) params.append('organization_id', oid);

      // Add timeout to prevent long waits (axios may hang if TM is unreachable)
      const response = await this.withTimeout(
        workqueueClient.get(`/api/v1/workqueue/items?${params.toString()}`, {
          timeout: 10000,
        }),
        12000,
        () => null
      );

      if (!response) {
        return [];
      }
      if (response.items && Array.isArray(response.items)) {
        return applyAssignmentTypeFilter(
          applyActiveWorkQueueStatusFilter(mapItemsForShell(response.items), filters.status),
          filters.assigned_to_type
        );
      }
      if (Array.isArray(response)) {
        return applyAssignmentTypeFilter(
          applyActiveWorkQueueStatusFilter(mapItemsForShell(response), filters.status),
          filters.assigned_to_type
        );
      }
      return [];
    } catch (error) {
      return [];
    }
  },
  
  // Get queue items from a specific RCM service (denials, EOB exceptions, or work-queue API)
  async getQueueItemsFromService(serviceKey, filters = {}) {
    return getQueueItemsFromRcmService(serviceKey, filters);
  },

  /**
   * TaskManager + all RCM service queues for one org scope, deduped, newest first.
   * @param {string|null|undefined} orgIdOverride — when set, Task Manager + RCM calls use this organization_id
   */
  async _getCombinedQueueItemsSingleOrg(
    filters = {},
    orgIdOverride = null,
    { allowMockFallback = useWorkQueueMock() } = {}
  ) {
    const serviceKeys = Object.keys(RCM_SERVICES);
    const rcmPromises = serviceKeys.map((serviceKey) =>
      this.withTimeout(
        getQueueItemsFromRcmService(serviceKey, filters, orgIdOverride || undefined),
        10000,
        () => []
      ).catch(() => [])
    );
    const [tmItems, ...rcmResults] = await Promise.all([
      this._getTaskManagerQueueItemsRaw(filters, orgIdOverride),
      ...rcmPromises,
    ]);
    const rcmItems = rcmResults.flat();
    const tmTagged = (tmItems || []).map((item) => ({
      ...item,
      organization_id: item.organization_id || orgIdOverride,
      source_service: item.source_service || 'taskmanager',
      source_service_name: item.source_service_name || 'Task Manager',
    }));
    const rcmTagged = rcmItems.map((item) => ({
      ...item,
      organization_id: item.organization_id || orgIdOverride,
    }));
    const merged = [...tmTagged, ...rcmTagged];
    const seen = new Set();
    const deduped = [];
    for (const item of merged) {
      const key =
        orgIdOverride != null && orgIdOverride !== ''
          ? `${orgIdOverride}|${canonicalQueueDedupKey(item)}`
          : canonicalQueueDedupKey(item);
      if (seen.has(key)) continue;
      seen.add(key);
      deduped.push(item);
    }
    deduped.sort((a, b) => {
      const da = new Date(a.created_at || 0).getTime();
      const db = new Date(b.created_at || 0).getTime();
      return db - da;
    });
    // Never invent demo rows — empty queue is a valid production state.
    void allowMockFallback;
    return applyAssignmentTypeFilter(deduped, filters.assigned_to_type);
  },

  /**
   * TaskManager + all RCM service queues, deduped by source_service|id, newest first.
   * @param {object} filters
   * @param {string[]|null|undefined} organizationIds — when 2+ ids, fans out per org (Agency). When omitted, uses localStorage org (RCM).
   */
  async getCombinedQueueItems(filters = {}, organizationIds = null) {
    try {
      if (Array.isArray(organizationIds) && organizationIds.length === 0) {
        return [];
      }
      if (Array.isArray(organizationIds) && organizationIds.length > 1) {
        const parts = await Promise.all(
          organizationIds.map((oid) =>
            this._getCombinedQueueItemsSingleOrg(
              filters,
              oid != null ? String(oid) : null,
              { allowMockFallback: false }
            )
          )
        );
        const flat = parts.flat();
        flat.sort((a, b) => {
          const da = new Date(a.created_at || 0).getTime();
          const db = new Date(b.created_at || 0).getTime();
          return db - da;
        });
        return flat;
      }
      const single =
        Array.isArray(organizationIds) && organizationIds.length === 1
          ? String(organizationIds[0])
          : null;
      return await this._getCombinedQueueItemsSingleOrg(filters, single, {
        allowMockFallback: false,
      });
    } catch (error) {
      console.warn('[WorkQueue] getCombinedQueueItems error:', error?.message || error);
      return [];
    }
  },
  
  // Deprecated — production queues never invent demo rows
  _mockTeamMemberDisplayNames: null,
  setMockTeamMemberDisplayNames(names) {
    this._mockTeamMemberDisplayNames = Array.isArray(names) && names.length ? names : null;
  },

  /** @deprecated Mock rows removed; always returns []. Kept for call-site compatibility. */
  generateMockWorkQueueItems(_count = 50) {
    return [];
  },

  // Get queue items from all services
  async getQueueItemsFromAllServices(filters = {}) {
    try {
      const serviceKeys = Object.keys(RCM_SERVICES);
      
      const promises = serviceKeys.map(serviceKey => 
        this.withTimeout(
          this.getQueueItemsFromService(serviceKey, filters),
          10000,
          () => []
        ).catch(error => {
          // Silently handle network errors - these are expected when services aren't running
          const isNetworkError = error.code === 'ERR_NETWORK' || 
                                error.code === 'ERR_CONNECTION_REFUSED' ||
                                error.code === 'ERR_CONNECTION_RESET' ||
                                error.message?.includes('Network Error') ||
                                error.message?.includes('Connection refused') ||
                                error.message?.includes('Connection reset') ||
                                error.message?.includes('timeout');
          
          if (!isNetworkError) {
            console.warn(`[${serviceKey}] Error fetching queue items:`, error.message);
          }
          return [];
        })
      );
      
      // Use Promise.allSettled to not wait for all services, but still get results
      const results = await Promise.allSettled(promises);
      const allItems = results
        .filter(result => result.status === 'fulfilled')
        .map(result => result.value)
        .flat();
      
      return allItems;
    } catch (error) {
      return [];
    }
  },

  // Get a specific queue item
  async getQueueItem(itemId) {
    return workqueueClient.get(`/api/v1/workqueue/items/${itemId}`);
  },

  /** Append-only lifecycle / audit trail (oldest → newest). */
  async getQueueItemActivity(itemId) {
    if (!itemId) return [];
    try {
      const data = await workqueueClient.get(`/api/v1/workqueue/items/${itemId}/activity`);
      return Array.isArray(data) ? data : data?.items || [];
    } catch (e) {
      console.warn('[WorkQueue] Failed to load activity:', e?.message || e);
      return [];
    }
  },

  _shellActorPayload() {
    try {
      const actor = typeof getShellActor === 'function' ? getShellActor() : null;
      if (actor && typeof actor === 'object') {
        return {
          actor_id: actor.id || actor.user_id || actor.sub || null,
          actor_name:
            actor.name ||
            actor.display_name ||
            actor.email ||
            actor.id ||
            null,
          actor_type: actor.type || 'human',
        };
      }
    } catch (_) {
      /* ignore */
    }
    const tokenUser =
      localStorage.getItem('user_name') ||
      localStorage.getItem('userName') ||
      localStorage.getItem('user_id') ||
      null;
    return {
      actor_id: localStorage.getItem('user_id') || tokenUser,
      actor_name: tokenUser,
      actor_type: 'human',
    };
  },

  // Create a new queue item
  async createQueueItem(itemData) {
    const actor = this._shellActorPayload();
    const organizationId =
      itemData.organization_id ||
      localStorage.getItem('selectedOrganizationId') ||
      localStorage.getItem('organizationId') ||
      localStorage.getItem('organization_id');
    const body = {
      ...itemData,
      item_metadata: {
        ...(itemData.item_metadata || {}),
        created_by: actor.actor_id,
        created_by_name: actor.actor_name,
        actor_type: actor.actor_type,
      },
    };
    return workqueueClient.post('/api/v1/workqueue/items', body, {
      params: organizationId ? { organization_id: organizationId } : undefined,
    });
  },

  async deleteQueueItem(itemId, options = {}) {
    const organizationId =
      options.organization_id ||
      localStorage.getItem('selectedOrganizationId') ||
      localStorage.getItem('organizationId') ||
      localStorage.getItem('organization_id');
    const actor = this._shellActorPayload();
    const actor_role =
      options.actor_role ||
      localStorage.getItem('current_user_role') ||
      '';
    return workqueueClient.delete(`/api/v1/workqueue/items/${itemId}`, {
      params: {
        ...(organizationId ? { organization_id: organizationId } : {}),
        actor_role,
        actor_id: options.actor_id || actor.actor_id,
        actor_name: options.actor_name || actor.actor_name,
      },
    });
  },

  // Update a queue item (Task Manager + owning service writeback)
  async updateQueueItem(itemId, updates, sourceService = null, itemSnapshot = null) {
    await this._maybeEnforceComplianceGuardrail('update', {
      updates,
      itemSnapshot,
      extra: { actor: getShellActor(), action: updates?.status || 'update' },
    });

    let resolvedId = itemId;
    if (!isUuidString(String(itemId)) && itemSnapshot) {
      resolvedId = await this.ensureAggregatedItemInTaskManager(itemId, itemSnapshot);
    }

    const src = normalizeQueueSourceService(
      sourceService ||
        itemSnapshot?.source_service ||
        itemSnapshot?.external_service ||
        itemSnapshot?.target_service ||
        null
    );
    const domainId =
      extractDomainIdFromQueueItem(itemSnapshot) ||
      extractDomainIdFromQueueItem({ id: itemId, metadata: itemSnapshot?.metadata });

    // Default: update via TaskManager (pushes to service via sync_to_service)
    const actor = this._shellActorPayload();
    const tmResult = await workqueueClient.patch(`/api/v1/workqueue/items/${resolvedId}`, {
      ...updates,
      actor_id: updates.actor_id || actor.actor_id,
      actor_name: updates.actor_name || actor.actor_name,
      actor_type: updates.actor_type || actor.actor_type,
    });

    // Dual-write to owning service so service queues reflect status immediately
    if (src && domainId && (updates.status || updates.notes !== undefined || updates.priority || updates.action_taken !== undefined)) {
      await writebackToOwningService(src, domainId, {
        patchBody: {
          status: updates.status,
          notes: updates.notes,
          priority: updates.priority,
          action_taken: updates.action_taken,
        },
      });
    }

    broadcastWorkQueueChanged({
      action: 'update',
      sourceService: src,
      itemId: String(resolvedId),
      display_queue_id: itemSnapshot?.display_queue_id || itemSnapshot?.metadata?.display_queue_id,
    });
    return tmResult;
  },

  /**
   * Build Task Manager QueueItemCreate body for an aggregated RCM row (not yet persisted in TM).
   */
  buildQueueItemCreatePayload(uiId, snapshot = {}) {
    const src = snapshot.source_service || snapshot.target_service || 'claims_processing';
    const map = {
      denial_management: { type: 'denial', target_service: 'denials' },
      remittance_advice: { type: 'remittance', target_service: 'remittance_advice' },
      insurance_verification: { type: 'insurance_verification', target_service: 'insurance_verification' },
      prior_authorization: { type: 'prior_auth', target_service: 'prior_auth' },
      claims_processing: { type: 'claims_processing', target_service: 'claims_processing' },
      ar_posting: { type: 'ar_posting', target_service: 'ar_posting' },
      charge_capture: { type: 'charge_capture', target_service: 'charge_capture' },
      patient_billing: { type: 'patient_billing', target_service: 'patient_billing' },
      contract_management: { type: 'contract_management', target_service: 'contract_management' },
      rcm_compliance: { type: 'rcm_compliance', target_service: 'rcm_compliance' },
    };
    const m = map[src] || { type: 'claims_processing', target_service: 'claims_processing' };
    return {
      type: m.type,
      external_id: String(uiId),
      external_service: src,
      item_data: {
        title: snapshot.title || 'Work queue item',
        description: snapshot.description || '',
        source_snapshot: snapshot.metadata || {},
        claim_id: snapshot.metadata?.domain_id || snapshot.metadata?.claim_id,
      },
      item_metadata: {
        aggregated_ui_id: uiId,
        source_service: src,
        display_queue_id: snapshot.display_queue_id || snapshot.metadata?.display_queue_id,
        domain_id: snapshot.metadata?.domain_id,
      },
      priority: 'normal',
      status: 'pending',
      target_service: m.target_service,
    };
  },

  async findTaskManagerItemByExternalId(externalId) {
    if (!externalId) return null;
    const data = await workqueueClient.get('/api/v1/workqueue/items', {
      params: { external_id: String(externalId), limit: 5 },
    });
    const items = Array.isArray(data) ? data : data?.items || [];
    return items[0] || null;
  },

  /** Ensure a Task Manager row exists for aggregated UI ids; returns TM UUID string. */
  async ensureAggregatedItemInTaskManager(uiId, snapshot = {}) {
    if (!uiId) {
      throw new Error('Missing queue item id');
    }
    const raw = String(uiId).trim();
    if (isUuidString(raw)) {
      return raw;
    }
    const existing = await this.findTaskManagerItemByExternalId(raw);
    if (existing?.id) {
      return existing.id;
    }
    const body = this.buildQueueItemCreatePayload(raw, snapshot);
    try {
      const created = await workqueueClient.post('/api/v1/workqueue/items', body);
      if (created?.id) {
        return created.id;
      }
      throw new Error('Task Manager create response missing id');
    } catch (e) {
      const again = await this.findTaskManagerItemByExternalId(raw);
      if (again?.id) {
        return again.id;
      }
      throw e;
    }
  },

  // Assign a queue item (Task Manager + Denial/AR writeback)
  async assignQueueItem(itemId, assignment, sourceService = null, itemSnapshot = null) {
    await this._maybeEnforceComplianceGuardrail('assign', {
      assignment,
      itemSnapshot,
      extra: { actor: assignment?.assigned_by || getShellActor() },
    });

    let resolvedId = itemId;
    if (!isUuidString(String(itemId))) {
      if (!itemSnapshot || typeof itemSnapshot !== 'object') {
        throw new Error(
          'This work item must be registered in Task Manager before assignment. Reload the work queue or select the item again.'
        );
      }
      resolvedId = await this.ensureAggregatedItemInTaskManager(itemId, itemSnapshot);
    }

    const src = normalizeQueueSourceService(
      sourceService ||
        itemSnapshot?.source_service ||
        itemSnapshot?.external_service ||
        itemSnapshot?.target_service ||
        null
    );
    const domainId =
      extractDomainIdFromQueueItem(itemSnapshot) ||
      extractDomainIdFromQueueItem({ id: itemId, metadata: itemSnapshot?.metadata });

    const result = await workqueueClient.post(
      `/api/v1/workqueue/items/${resolvedId}/assign`,
      assignment
    );

    if (src && domainId) {
      await writebackToOwningService(src, domainId, {
        assignBody: {
          assigned_to: assignment.assigned_to,
          assigned_by: assignment.assigned_by || 'shell',
          assigned_to_type: assignment.assigned_to_type,
          assigned_to_name: assignment.assigned_to_name,
          reason: assignment.reason,
          status: 'assigned',
        },
      });
    }

    broadcastWorkQueueChanged({
      action: 'assign',
      sourceService: src,
      itemId: String(resolvedId),
      display_queue_id: itemSnapshot?.display_queue_id || itemSnapshot?.metadata?.display_queue_id,
    });
    return result;
  },

  // Bulk assign queue items (resolves aggregated ids via Task Manager)
  async bulkAssignQueueItems(assignment, itemsById = {}) {
    if (assignment?.assigned_to_type === 'ai') {
      const rawIds = assignment.queue_item_ids || [];
      for (const raw of rawIds) {
        const idStr = String(raw);
        const snap = itemsById[idStr] || itemsById[raw];
        if (snap) {
          await this._maybeEnforceComplianceGuardrail('bulk_assign', {
            assignment,
            itemSnapshot: snap,
            extra: { actor: assignment?.assigned_by || getShellActor() },
          });
        }
      }
    }

    const rawIds = assignment.queue_item_ids || [];
    const resolved = [];
    for (const raw of rawIds) {
      const idStr = String(raw);
      if (isUuidString(idStr)) {
        resolved.push(idStr);
      } else {
        const snap = itemsById[idStr] || itemsById[raw];
        if (!snap || typeof snap !== 'object') {
          throw new Error(
            `Missing item context for id "${idStr}". Select items from the list and try again.`
          );
        }
        const rid = await this.ensureAggregatedItemInTaskManager(idStr, snap);
        resolved.push(rid);
      }
    }
    const bulkResult = await workqueueClient.post('/api/v1/workqueue/items/bulk-assign', {
      ...assignment,
      queue_item_ids: resolved,
    });
    broadcastWorkQueueChanged({ action: 'bulk_assign', count: resolved.length });
    return bulkResult;
  },

  // Get queue statistics (returns null when Task Manager is down — avoids throwing into WorkQueue UI)
  async getQueueStats(organizationId = null) {
    try {
      return await workqueueClient.get(`/api/v1/workqueue/stats`);
    } catch (error) {
      if (!isTaskManagerUnreachableError(error) && !isSoftWorkQueueHttpError(error)) {
        console.warn('[WorkQueue] getQueueStats:', error?.message || error);
      }
      return null;
    }
  },

  // Sync a queue item
  async syncQueueItem(itemId) {
    return workqueueClient.post(`/api/v1/workqueue/sync/${itemId}`);
  },

  // Sync work queue items from all RCM services
  async syncFromServices() {
    return workqueueClient.post('/api/v1/workqueue/sync/from-services');
  },
  
  // Create a queue item in a specific service
  async createQueueItemInService(serviceKey, itemData) {
    try {
      const service = RCM_SERVICES[serviceKey];
      if (!service) {
        throw new Error(`Unknown service: ${serviceKey}`);
      }
      
      const client = createServiceClient(serviceKey);
      if (!client) {
        throw new Error(`Could not create client for service: ${serviceKey}`);
      }
      
      // Add organization_id if not in itemData
      if (!itemData.organization_id) {
        const organizationId = localStorage.getItem('selectedOrganizationId') || 
                               localStorage.getItem('organizationId');
        if (organizationId) {
          itemData.organization_id = organizationId;
        }
      }
      
      return await client.post(service.endpoint, itemData);
    } catch (error) {
      console.error(`Error creating queue item in ${serviceKey}:`, error);
      throw error;
    }
  },
  
  // Delete a queue item from a specific service
  async deleteQueueItemFromService(serviceKey, itemId) {
    try {
      const service = RCM_SERVICES[serviceKey];
      if (!service) {
        throw new Error(`Unknown service: ${serviceKey}`);
      }
      
      const client = createServiceClient(serviceKey);
      if (!client) {
        throw new Error(`Could not create client for service: ${serviceKey}`);
      }
      
      return await client.delete(`${service.endpoint}${itemId}`);
    } catch (error) {
      console.error(`Error deleting queue item from ${serviceKey}:`, error);
      throw error;
    }
  },
  
  // Get available services
  getAvailableServices() {
    return Object.keys(RCM_SERVICES).map(key => ({
      key,
      name: RCM_SERVICES[key].name,
      port: RCM_SERVICES[key].port,
    }));
  },
};

export { isTaskManagerUnreachableError };

